import React, { useEffect, useState } from "react";
import { ArrowLeft, Edit3, Trash2, Package, Calendar, DollarSign, Hash, FileText } from "lucide-react";
import "./ProductDetailsPage.css";
import { useNavigate, useParams } from "react-router-dom";
import {
  getProductById,
  updateProduct,
  deleteProduct,
  getProductApiErrorMessage,
  ProductDetailResponse,
} from "../../services/productService";
import ProductFormModal, {
  ProductFormInitialValues,
} from "../../compoonents/ProductFormModal/ProductFormModal";
import defaultProductImage from "../../assets/images/empty.png";
import { resolveMediaUrl } from "../../utils/mediaUrl";
import { formatProductStatus, normalizeProductStatusForApi } from "../../constants/productStatus";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ProductDetailsPage: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const navigate = useNavigate();
  const { productId } = useParams<{ productId: string }>();
  const [productData, setProductData] = useState<ProductDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const product = productData?.product;
  const images = product?.image ?? [];

  const loadProduct = async () => {
    if (!productId) {
      setError("Product ID is missing");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getProductById(productId);
      setProductData(data);
      setCurrentImageIndex(0);
    } catch {
      setError("Failed to fetch product details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProduct();
  }, [productId]);

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const getStatusColor = (status: string | undefined): string => {
    const normalized = (status || "").toLowerCase();
    if (normalized.includes("in_stock") || normalized === "in stock") return "#10b981";
    if (normalized.includes("out") || normalized === "disabled") return "#ef4444";
    return "#6b7280";
  };

  const editInitialValues: ProductFormInitialValues | undefined = product
    ? {
        name: product.name,
        price: product.price,
        quantity: product.quantity,
        description: product.description || "",
        productStatus: normalizeProductStatusForApi(
          String(product.productStatus)
        ) as string,
      }
    : undefined;

  const handleEditSave = async (formData: FormData) => {
    if (!productId) return;
    setSaving(true);
    try {
      await updateProduct(productId, formData);
      toast.success("Product updated");
      setShowEditModal(false);
      await loadProduct();
    } catch (err) {
      toast.error(getProductApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!productId) return;
    if (!window.confirm("Delete this product permanently?")) return;
    try {
      await deleteProduct(productId);
      toast.success("Product deleted");
      navigate("/purchase-orders");
    } catch (err) {
      toast.error(getProductApiErrorMessage(err));
    }
  };

  const nextImage = (): void => {
    if (!images.length) return;
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (): void => {
    if (!images.length) return;
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (loading) {
    return <div className="product-details-container">Loading...</div>;
  }

  if (error || !product) {
    return (
      <div className="product-details-container">
        <p>{error || "Product not found"}</p>
        <button type="button" onClick={() => navigate("/purchase-orders")}>
          Back to Products
        </button>
      </div>
    );
  }

  const statusColor = getStatusColor(String(product.productStatus));
  const statusLabel = formatProductStatus(String(product.productStatus));

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="product-details-container">
        <div className="product-header">
          <div className="header-left">
            <button
              type="button"
              className="back-button"
              onClick={() => navigate("/purchase-orders")}
            >
              <ArrowLeft size={16} />
              Back to Products
            </button>
            <h1 className="header-title">Product Details</h1>
          </div>

          <div className="header-buttons">
            <button
              type="button"
              className="edit-button"
              onClick={() => setShowEditModal(true)}
            >
              <Edit3 size={16} />
              Edit Product
            </button>
            <button type="button" className="delete-button" onClick={handleDelete}>
              <Trash2 size={16} />
              Delete
            </button>
          </div>
        </div>

        <div className="product-content-con">
          <div className="images-container">
            <h3 className="images-title">Product Images</h3>
            <div className="main-image-container">
              <img
                src={
                  resolveMediaUrl(images[currentImageIndex]) || defaultProductImage
                }
                alt={product.name}
                className="main-image"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = defaultProductImage;
                }}
              />
              {images.length > 1 && (
                <>
                  <button type="button" onClick={prevImage} className="nav-button prev-button">
                    ‹
                  </button>
                  <button type="button" onClick={nextImage} className="nav-button next-button">
                    ›
                  </button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="thumbnails-container">
                {images.map((img: string, index: number) => (
                  <img
                    key={index}
                    src={resolveMediaUrl(img) || defaultProductImage}
                    alt={`${product.name} ${index + 1}`}
                    onClick={() => setCurrentImageIndex(index)}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = defaultProductImage;
                    }}
                    className={`thumbnail ${
                      index === currentImageIndex
                        ? "thumbnail-active"
                        : "thumbnail-inactive"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="info-column">
            <div className="info-card">
              <h2 className="product-name">{product.name}</h2>
              <div className="info-items-container">
                <div className="info-item">
                  <DollarSign size={20} className="info-icon" />
                  <span className="info-text">Price:</span>
                  <span className="price-text">₦{product.price}</span>
                </div>
                <div className="info-item">
                  <Package size={20} className="info-icon" />
                  <span className="info-text">Quantity:</span>
                  <span className="quantity-text">{product.quantity}</span>
                </div>
                <div className="info-item">
                  <div
                    className="status-indicator"
                    style={{ backgroundColor: statusColor }}
                  />
                  <span className="info-text">Status:</span>
                  <span
                    className="status-text"
                    style={{
                      color: statusColor,
                      backgroundColor: `${statusColor}20`,
                    }}
                  >
                    {statusLabel}
                  </span>
                </div>
              </div>
            </div>

            <div className="info-card">
              <div className="description-header">
                <FileText size={20} className="info-icon" />
                <h3 className="description-title">Description</h3>
              </div>
              <p className="description-text">{product.description || "—"}</p>
            </div>

            <div className="info-card">
              <h3 className="metadata-title">Product Information</h3>
              <div className="metadata-items-container">
                <div className="metadata-item">
                  <Hash size={18} className="metadata-icon" />
                  <span className="metadata-label">Product ID:</span>
                  <span className="metadata-value product-id">{product.id}</span>
                </div>
                <div className="metadata-item">
                  <Calendar size={18} className="metadata-icon" />
                  <span className="metadata-label">Created:</span>
                  <span className="metadata-value">
                    {formatDate(product.createdAt)}
                  </span>
                </div>
                <div className="metadata-item">
                  <Calendar size={18} className="metadata-icon" />
                  <span className="metadata-label">Updated:</span>
                  <span className="metadata-value">
                    {formatDate(product.updatedAt)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showEditModal && editInitialValues && (
        <ProductFormModal
          mode="edit"
          initialValues={editInitialValues}
          onClose={() => setShowEditModal(false)}
          onSave={handleEditSave}
          loading={saving}
        />
      )}
    </>
  );
};

export default ProductDetailsPage;
